import React from "react";
import {
  Annotation,
  BookmarkView,
  ContextMenuItem,
  Inject,
  LinkAnnotation,
  Magnification,
  Navigation,
  PdfViewerComponent,
  TextSearch,
  TextSelection,
  ThumbnailView,
  Toolbar,
} from "@syncfusion/ej2-react-pdfviewer";
import type {
  AnnotationAddEventArgs,
  CustomContextMenuSelectEventArgs,
  RectangleSettings,
  TextSelectionEndEventArgs,
} from "@syncfusion/ej2-react-pdfviewer";
import Loading from "../Loading";
import { Button } from "../tailwind/Button";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { toasts } from "../toasts/toasts";

const PDF_VIEWER_RESOURCE_URL =
  "https://cdn.syncfusion.com/ej2/34.2.3/dist/ej2-pdfviewer-lib";
const COPY_REFERENCE_MENU_ID = "copy-reference-link";

/** Syncfusion annotation bounds use 96dpi page pixels; query rects use PDF points. */
const PDF_POINT_TO_PIXEL = 96 / 72;
const PIXEL_TO_PDF_POINT = 72 / 96;

interface SyncfusionPdfViewerProps {
  url: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
  height?: string;
}

interface SelectedReference {
  pageIndex: number;
  rectsOnPage: number[][];
}

type SelectionBound = {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  pageIndex?: number;
};

function roundCoord(value: number) {
  return Math.round(value * 1000) / 1000;
}

function pixelsToPdfRects(bounds: SelectionBound[]): number[][] {
  return bounds
    .filter(
      (bound) =>
        bound &&
        [bound.left, bound.top, bound.width, bound.height].every(
          (value) => typeof value === "number" && Number.isFinite(value),
        ),
    )
    .map((bound) => [
      roundCoord((bound.left as number) * PIXEL_TO_PDF_POINT),
      roundCoord((bound.top as number) * PIXEL_TO_PDF_POINT),
      roundCoord((bound.width as number) * PIXEL_TO_PDF_POINT),
      roundCoord((bound.height as number) * PIXEL_TO_PDF_POINT),
    ]);
}

export default function SyncfusionPdfViewer({
  url,
  pageIndex,
  rectsOnPage,
  height = "calc(100vh - 220px)",
}: SyncfusionPdfViewerProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const viewerRef = React.useRef<PdfViewerComponent>(null);
  const viewerId = React.useId().replace(/:/g, "");
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDocumentLoaded, setIsDocumentLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [retryKey, setRetryKey] = React.useState(0);
  const queryHighlightIdsRef = React.useRef<string[]>([]);
  const selectedReferenceRef = React.useRef<SelectedReference | null>(null);
  const isApplyingQueryHighlightsRef = React.useRef(false);
  const appliedLocationKeyRef = React.useRef<string | null>(null);

  const locationKey = JSON.stringify({ pageIndex, rectsOnPage });

  React.useEffect(() => {
    setIsLoading(true);
    setIsDocumentLoaded(false);
    setLoadError(false);
    queryHighlightIdsRef.current = [];
    selectedReferenceRef.current = null;
    appliedLocationKeyRef.current = null;
  }, [url, retryKey]);

  // Syncfusion only listens to window resize. Redraw whenever our container
  // changes size (assistant panel, nav collapse, sidebar, etc.).
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !isDocumentLoaded) {
      return;
    }

    let rafId = 0;
    let lastWidth = 0;
    let lastHeight = 0;

    const redraw = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const { width, height: nextHeight } = container.getBoundingClientRect();
        if (
          Math.round(width) === Math.round(lastWidth) &&
          Math.round(nextHeight) === Math.round(lastHeight)
        ) {
          return;
        }
        lastWidth = width;
        lastHeight = nextHeight;
        viewerRef.current?.updateViewerContainer();
      });
    };

    const observer = new ResizeObserver(redraw);
    observer.observe(container);
    redraw();

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [isDocumentLoaded, retryKey]);

  const captureSelectionReference = React.useCallback(() => {
    const viewer = viewerRef.current as PdfViewerComponent & {
      textSelectionModule?: {
        selectionRangeArray?: Array<{
          pageNumber: number;
          rectangleBounds?: SelectionBound[];
        }>;
      };
    };

    const ranges = viewer?.textSelectionModule?.selectionRangeArray;
    if (ranges?.length) {
      const pageNumber = ranges[ranges.length - 1]?.pageNumber;
      const bounds = ranges.flatMap((range) => range.rectangleBounds ?? []);
      const rects = pixelsToPdfRects(bounds);

      if (
        typeof pageNumber === "number" &&
        Number.isInteger(pageNumber) &&
        pageNumber >= 0 &&
        rects.length > 0
      ) {
        selectedReferenceRef.current = {
          pageIndex: pageNumber,
          rectsOnPage: rects,
        };
        return selectedReferenceRef.current;
      }
    }

    return selectedReferenceRef.current;
  }, []);

  const clearQueryHighlights = React.useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer?.annotation) return;

    queryHighlightIdsRef.current.forEach((annotationId) => {
      try {
        viewer.annotation.deleteAnnotationById(annotationId);
      } catch {
        // Annotation may already have been cleared with the document.
      }
    });
    queryHighlightIdsRef.current = [];
  }, []);

  React.useEffect(() => {
    if (!isDocumentLoaded) return;

    const viewer = viewerRef.current;
    if (!viewer?.annotation) return;

    if (appliedLocationKeyRef.current === locationKey) return;

    const hasPage =
      pageIndex !== undefined && Number.isInteger(pageIndex) && pageIndex >= 0;
    const hasRects = !!rectsOnPage?.length;

    clearQueryHighlights();

    if (hasPage) {
      viewer.navigation.goToPage(pageIndex + 1);
    }

    if (!hasPage || !hasRects) {
      appliedLocationKeyRef.current = locationKey;
      return;
    }

    // Wait for Syncfusion to finish navigating/rendering the target page.
    // Do not re-run this on pageChange — that fights user scrolling.
    const timeoutId = window.setTimeout(() => {
      const currentViewer = viewerRef.current;
      if (!currentViewer?.annotation) return;

      try {
        isApplyingQueryHighlightsRef.current = true;

        // Rectangle annotations are more reliable than text-markup Highlight
        // for absolute PDF-point bounds from search/reference links.
        for (const [x, y, width, rectHeight] of rectsOnPage) {
          currentViewer.annotation.addAnnotation("Rectangle", {
            pageNumber: pageIndex + 1,
            offset: {
              x: x * PDF_POINT_TO_PIXEL,
              y: y * PDF_POINT_TO_PIXEL,
            },
            width: width * PDF_POINT_TO_PIXEL,
            height: rectHeight * PDF_POINT_TO_PIXEL,
            fillColor: "#ffff00",
            strokeColor: "#ffff00",
            thickness: 0,
            opacity: 0.35,
            isLock: true,
          } as unknown as RectangleSettings);
        }

        appliedLocationKeyRef.current = locationKey;
      } catch (error) {
        console.error("Failed to apply PDF highlight annotations", error);
      } finally {
        isApplyingQueryHighlightsRef.current = false;
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    clearQueryHighlights,
    isDocumentLoaded,
    locationKey,
    pageIndex,
    rectsOnPage,
  ]);

  const handleAnnotationAdd = React.useCallback(
    (args: AnnotationAddEventArgs) => {
      if (
        args.annotationType === "Rectangle" &&
        isApplyingQueryHighlightsRef.current
      ) {
        queryHighlightIdsRef.current.push(args.annotationId);
      }
    },
    [],
  );

  const handleCustomContextMenuSelect = React.useCallback(
    async (args: CustomContextMenuSelectEventArgs) => {
      if (args.id !== COPY_REFERENCE_MENU_ID) return;

      try {
        const referenceUrl = new URL(window.location.href);
        const selectedReference = captureSelectionReference();

        if (selectedReference) {
          referenceUrl.searchParams.set(
            "pageIndex",
            selectedReference.pageIndex.toString(),
          );
          referenceUrl.searchParams.set(
            "rectsOnPage",
            JSON.stringify(selectedReference.rectsOnPage),
          );
        }

        await navigator.clipboard.writeText(referenceUrl.toString());
        toasts.success(
          selectedReference
            ? "Reference link copied"
            : "Page link copied (no text selected)",
        );
      } catch {
        toasts.error("Failed to copy reference link");
      }
    },
    [captureSelectionReference],
  );

  const handleTextSelectionEnd = React.useCallback(
    (args: TextSelectionEndEventArgs) => {
      if (!args.textContent?.trim() || !Array.isArray(args.textBounds)) {
        selectedReferenceRef.current = null;
        return;
      }

      const bounds = (args.textBounds as SelectionBound[]).filter(
        (bound) =>
          bound &&
          (bound.pageIndex === undefined ||
            Number(bound.pageIndex) === Number(args.pageIndex)),
      );
      const nextRects = pixelsToPdfRects(bounds);

      selectedReferenceRef.current = nextRects.length
        ? {
            pageIndex: Math.max(0, Number(args.pageIndex) - 1),
            rectsOnPage: nextRects,
          }
        : null;
    },
    [],
  );

  return (
    <div
      ref={containerRef}
      className="relative isolate z-0 h-full w-full min-w-0 overflow-hidden"
    >
      <div
        className={
          isLoading || loadError
            ? "h-full opacity-0 pointer-events-none"
            : "h-full opacity-100"
        }
      >
        <PdfViewerComponent
          key={`${url}-${retryKey}`}
          ref={viewerRef}
          id={`syncfusion-pdf-viewer-${viewerId}`}
          documentPath={url}
          resourceUrl={PDF_VIEWER_RESOURCE_URL}
          height={height}
          width="100%"
          enableAnnotation
          enableAnnotationToolbar={false}
          enableDownload={false}
          enableFormDesigner={false}
          enablePrint={false}
          contextMenuSettings={{
            contextMenuAction: "RightClick",
            contextMenuItems: [ContextMenuItem.Copy],
          }}
          customContextMenuSelect={handleCustomContextMenuSelect}
          textSelectionEnd={handleTextSelectionEnd}
          toolbarSettings={{
            toolbarItems: [
              "PageNavigationTool",
              "MagnificationTool",
              "PanTool",
              "SelectionTool",
              "SearchOption",
            ],
          }}
          documentLoad={() => {
            viewerRef.current?.addCustomMenu(
              [
                {
                  id: COPY_REFERENCE_MENU_ID,
                  text: "Copy reference link",
                },
              ],
              false,
              true,
            );
            setIsLoading(false);
            setIsDocumentLoaded(true);
          }}
          documentLoadFailed={() => {
            setIsLoading(false);
            setIsDocumentLoaded(false);
            setLoadError(true);
          }}
          ajaxRequestFailed={() => {
            setIsLoading(false);
            setIsDocumentLoaded(false);
            setLoadError(true);
          }}
          annotationAdd={handleAnnotationAdd}
        >
          <Inject
            services={[
              Toolbar,
              Magnification,
              Navigation,
              Annotation,
              LinkAnnotation,
              BookmarkView,
              ThumbnailView,
              TextSelection,
              TextSearch,
            ]}
          />
        </PdfViewerComponent>
      </div>

      {isLoading && !loadError ? (
        <div className="absolute inset-0 z-[1] flex items-center justify-center bg-white">
          <Loading />
        </div>
      ) : null}

      {loadError ? (
        <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-white px-8 text-center">
          <ExclamationTriangleIcon
            className="mb-4 h-12 w-12 shrink-0 text-amber-500"
            aria-hidden
          />
          <p className="max-w-md text-sm font-medium text-gray-700">
            We couldn&apos;t load this document. Try again in a moment.
          </p>
          <div className="mt-5">
            <Button
              variant="neutral"
              text="Try again"
              onClick={() => setRetryKey((key) => key + 1)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
